import { makeAutoObservable, runInAction } from "mobx";
import api from "../Utils/api";
import { setDeckRate, revertForOverride } from "../Utils/djAudioController";

const RATE_MIN = 0.8;
const RATE_MAX = 1.2;
const clampRate = (r) => Math.max(RATE_MIN, Math.min(RATE_MAX, r));

const emptyDeck = () => ({
  track: null,
  analysis: null,
  analysisStatus: null,
  currentTimeMs: 0,
  isPlaying: false,
  userRate: 1,
  effectiveRate: 1,
});

class DjStore {
  deckA = emptyDeck();
  deckB = emptyDeck();
  crossfade = 0;
  autoTransition = false;
  queue = [];
  queueIndex = 0;
  queueMode = false;
  playedIds = [];
  nextTrack = null;
  strategyOverride = "auto";
  featureCache = {};
  lastPickBreakdown = null;
  transitionFading = false;
  prewarmToken = 0;
  // Crowd-submit: links never auto-expire (manual revoke only). The DJ feed is
  // user-scoped — finished tracks auto-append to the queue; pending/failed
  // submissions + the active-link list drive the share modal.
  submissionLinks = [];
  submissionPending = [];
  submissionCurrentToken = null;
  submissionMaxLinks = 1;
  _feedPollTimer = null;

  constructor() {
    makeAutoObservable(this, { _feedPollTimer: false });
  }

  get submissionCurrentLink() {
    return (
      this.submissionLinks.find((l) => l.token === this.submissionCurrentToken) ||
      null
    );
  }

  startSubmissionFeed = () => {
    this.stopSubmissionFeed();
    this.pollSubmissionFeed();
    this._feedPollTimer = setInterval(() => this.pollSubmissionFeed(), 5000);
  };

  // Stops polling but leaves links active on the server (they persist).
  stopSubmissionFeed = () => {
    if (this._feedPollTimer) {
      clearInterval(this._feedPollTimer);
      this._feedPollTimer = null;
    }
  };

  pollSubmissionFeed = async () => {
    try {
      const { tracks, pending, links } = await api.getDjFeed();
      runInAction(() => {
        this.submissionLinks = links || [];
        this.submissionPending = pending || [];
        // Keep the current selection if it's still active; otherwise fall back
        // to the most recent link and sync the per-guest stepper to it.
        const tokens = new Set(this.submissionLinks.map((l) => l.token));
        if (
          !this.submissionCurrentToken ||
          !tokens.has(this.submissionCurrentToken)
        ) {
          this.submissionCurrentToken = this.submissionLinks[0]?.token || null;
          if (this.submissionCurrentLink)
            this.submissionMaxLinks = this.submissionCurrentLink.maxLinks;
        }
      });
      if (tracks && tracks.length) this.appendToQueue(tracks);
    } catch {
      // best-effort; ignore transient errors
    }
  };

  // Open-modal helper: make sure there's at least one link to show.
  ensureSubmissionLink = async () => {
    await this.pollSubmissionFeed();
    if (!this.submissionLinks.length) await this.createSubmissionLink();
  };

  createSubmissionLink = async () => {
    const { token } = await api.createDjSession(1);
    runInAction(() => {
      this.submissionCurrentToken = token;
      this.submissionMaxLinks = 1;
    });
    await this.pollSubmissionFeed();
    return token;
  };

  selectSubmissionLink = (token) => {
    const link = this.submissionLinks.find((l) => l.token === token);
    if (!link) return;
    this.submissionCurrentToken = token;
    this.submissionMaxLinks = link.maxLinks;
  };

  revokeSubmissionLink = async (token) => {
    try {
      await api.closeDjSession(token);
    } catch {
      // best-effort
    }
    if (this.submissionCurrentToken === token) {
      runInAction(() => {
        this.submissionCurrentToken = null;
      });
    }
    await this.pollSubmissionFeed();
  };

  // Per-guest cap for the currently shown link (updated in place).
  setSubmissionMaxLinks = (n) => {
    const clamped = Math.max(1, Math.min(20, Math.floor(Number(n) || 1)));
    this.submissionMaxLinks = clamped;
    const token = this.submissionCurrentToken;
    if (token) api.updateDjSession(token, clamped).catch(() => {});
  };

  setNextTrack = (track) => {
    this.nextTrack = track;
  };

  setStrategyOverride = (value) => {
    this.strategyOverride = value;
  };

  addPlayed = (mediaId) => {
    if (!this.playedIds.includes(mediaId)) this.playedIds.push(mediaId);
  };

  clearPlayed = () => {
    this.playedIds = [];
  };

  setFeatures = (mediaId, features) => {
    if (!mediaId || !features) return;
    const existing = this.featureCache[mediaId] || {};
    this.featureCache[mediaId] = { ...existing, ...features };
  };

  getFeatureCacheMap = () => {
    return new Map(Object.entries(this.featureCache));
  };

  setLastPickBreakdown = (breakdown) => {
    this.lastPickBreakdown = breakdown || null;
  };

  setQueue = (tracks) => {
    this.queue = tracks || [];
    this.prewarmQueue();
  };

  // Append fed tracks, skipping any whose mediaId is already queued (dedup).
  appendToQueue = (tracks) => {
    if (!tracks?.length) return;
    const existing = new Set(this.queue.map((t) => t.mediaId));
    const fresh = tracks.filter((t) => t?.mediaId && !existing.has(t.mediaId));
    if (!fresh.length) return;
    this.queue = [...this.queue, ...fresh];
    this.prewarmQueue();
  };

  removeFromQueue = (mediaId) => {
    this.queue = this.queue.filter((t) => t.mediaId !== mediaId);
  };

  // Reset the DJ queue and all queue-driven selection state (used on DJ-mode entry).
  clearQueue = () => {
    this.queue = [];
    this.queueIndex = 0;
    this.playedIds = [];
    this.nextTrack = null;
    this.queueMode = false;
  };

  prewarmQueue = async (concurrency = 4) => {
    this.prewarmToken += 1;
    const token = this.prewarmToken;
    const queue = this.queue.slice();
    const todo = queue.filter(
      (t) => t?.mediaId && !this.featureCache[t.mediaId]
    );
    if (!todo.length) return;

    let cursor = 0;
    const worker = async () => {
      while (cursor < todo.length) {
        if (this.prewarmToken !== token) return;
        const track = todo[cursor++];
        try {
          const analysis = await api.getSongAnalysis(track.mediaId);
          if (this.prewarmToken !== token) return;
          if (analysis?.status === "done") {
            runInAction(() => {
              this.setFeatures(track.mediaId, {
                bpm: analysis.bpm ?? null,
                camelot: analysis.camelot ?? null,
                energy: analysis.energy ?? null,
              });
            });
          }
        } catch {
          // best-effort; skip on error
        }
      }
    };

    const workers = Array.from(
      { length: Math.min(concurrency, todo.length) },
      worker
    );
    await Promise.all(workers);
  };

  setQueueIndex = (i) => {
    this.queueIndex = i;
  };

  setQueueMode = (on) => {
    this.queueMode = on;
  };

  loadToDeck = async (song, side) => {
    const prevDeck = side === "A" ? this.deckA : this.deckB;
    const prevTrack = prevDeck.track;
    const activeSide = this.deckA.isPlaying
      ? "A"
      : this.deckB.isPlaying
        ? "B"
        : null;
    const isOverrideOnIdleSide =
      this.queueMode && activeSide && side !== activeSide;

    if (isOverrideOnIdleSide && prevTrack && prevTrack.mediaId !== song.mediaId) {
      this.addPlayed(prevTrack.mediaId);
    }

    if (isOverrideOnIdleSide && this.transitionFading) {
      revertForOverride();
    }

    const initial = { ...emptyDeck(), track: song, analysisStatus: "loading" };
    initial.userRate = prevDeck.userRate ?? 1;
    initial.effectiveRate = prevDeck.userRate ?? 1;
    if (side === "A") this.deckA = initial;
    else this.deckB = initial;

    if (isOverrideOnIdleSide) {
      this.nextTrack = song;
    }

    try {
      const analysis = await api.getSongAnalysis(song.mediaId);
      runInAction(() => {
        if (analysis.status === "done") {
          this.setFeatures(song.mediaId, {
            bpm: analysis.bpm ?? null,
            camelot: analysis.camelot ?? null,
            energy: analysis.energy ?? null,
          });
        }
        const deck = side === "A" ? this.deckA : this.deckB;
        if (deck.track?.mediaId !== song.mediaId) return;
        const next = {
          ...deck,
          analysis: analysis.status === "done" ? analysis : null,
          analysisStatus: analysis.status || "missing",
        };
        if (side === "A") this.deckA = next;
        else this.deckB = next;
      });
    } catch {
      runInAction(() => {
        const deck = side === "A" ? this.deckA : this.deckB;
        const next = { ...deck, analysisStatus: "error" };
        if (side === "A") this.deckA = next;
        else this.deckB = next;
      });
    }
  };

  setDeckPlaying = (side, isPlaying) => {
    if (side === "A") this.deckA = { ...this.deckA, isPlaying };
    else this.deckB = { ...this.deckB, isPlaying };
  };

  setDeckTime = (side, currentTimeMs) => {
    if (side === "A") this.deckA = { ...this.deckA, currentTimeMs };
    else this.deckB = { ...this.deckB, currentTimeMs };
  };

  setCrossfade = (value) => {
    this.crossfade = value;
  };

  setAutoTransition = (on) => {
    this.autoTransition = on;
  };

  setTransitionFading = (on) => {
    this.transitionFading = on;
  };

  setUserRate = (side, rate) => {
    const clamped = clampRate(rate);
    if (side === "A") {
      this.deckA = { ...this.deckA, userRate: clamped, effectiveRate: clamped };
    } else {
      this.deckB = { ...this.deckB, userRate: clamped, effectiveRate: clamped };
    }
    setDeckRate(side, clamped);
  };

  setEffectiveRate = (side, rate) => {
    const clamped = clampRate(rate);
    if (side === "A") {
      this.deckA = { ...this.deckA, effectiveRate: clamped };
    } else {
      this.deckB = { ...this.deckB, effectiveRate: clamped };
    }
    setDeckRate(side, clamped);
  };

  restoreUserRate = (side) => {
    const deck = side === "A" ? this.deckA : this.deckB;
    this.setEffectiveRate(side, deck.userRate);
  };
}

const store = new DjStore();
export default store;
